import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ajv,
  fetchAddressFromCoordinates,
  OrganizationContext,
  useNewLabRequisition,
} from "hhn-commons";
import { LabRequistionTemplateForm, Loader } from "./NewLabRequisition.style";
import CommonDrawerHeader from "../common/CommonDrawerHeader";
import { LeftDrawerContext } from "../context/LeftDrawerContext";
import { LeftDrawerContainer } from "../layout/LeftDrawer.style";
import { DrawerBody } from "../layout/RightDrawer.style";
import { JsonForms } from "@jsonforms/react";
import renderers, { customCellRenderers } from "../message/templates/renderers";
import { Alert, Box, Button as LoadingButton } from "@mui/material";
import { IGroupInfo } from "@home-health-notify/domain";
import { GOOGLE_MAPS_API_KEY } from "../utils/GoogleMapsUtils";
import { useSnackbar } from "notistack";

type NewLabRequisitionType = {
  groupInfo: IGroupInfo;
  readonly: boolean;
  labRequisitionId?: string;
};

export default function NewLabRequisition({
  groupInfo,
  labRequisitionId,
  readonly,
}: NewLabRequisitionType) {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "labRequisition" });
  const { closeDrawer } = useContext(LeftDrawerContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();

  const {
    template,
    formData,
    handleChange,
    validationMode,
    additionalErrors,
    addAdditionalErrors,
    clearAdditionalErrors,
    isFormSubmitting,
    handleFormSubmit,
    hasNoLabs,
  } = useNewLabRequisition(
    closeDrawer,
    groupInfo,
    GOOGLE_MAPS_API_KEY,
    fetchAddressFromCoordinates,
    labRequisitionId,
  );

  return (
    <LeftDrawerContainer data-testid="new-lab-requisition">
      <CommonDrawerHeader
        showBackButton={false}
        onBackButtonClick={() => () => {}}
        title={`${tNs("labRequisition")} - ${groupInfo.name}`}
        onCloseButtonClick={() => {
          closeDrawer();
        }}
      />
      <DrawerBody>
        {!template && !hasNoLabs && <Loader></Loader>}
        {hasNoLabs && (
          <Alert
            severity="error"
            sx={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
            slotProps={{
              message: {
                sx: {
                  textAlign: "center",
                },
              },
            }}
          >
            {tNs("noLabsFound")}
          </Alert>
        )}
        {!!template && (
          <LabRequistionTemplateForm>
            <JsonForms
              schema={template.schema}
              uischema={template.uiSchema}
              data={formData}
              renderers={renderers}
              cells={customCellRenderers}
              onChange={handleChange}
              validationMode={validationMode}
              ajv={ajv}
              config={{
                selectedLab: selectedOrgUser?.labsManager.labDetails,
                addAdditionalErrors,
                clearAdditionalErrors,
              }}
              middleware={template.middleware}
              additionalErrors={additionalErrors}
              readonly={readonly}
            />
            <Box>
              <LoadingButton
                loading={isFormSubmitting}
                variant="contained"
                onClick={async () => {
                  const success = await handleFormSubmit();
                  if (!success) {
                    enqueueSnackbar(tNs("validationError"), {
                      variant: "error",
                    });
                    setTimeout(() => {
                      const firstError = document.querySelector(".Mui-error");
                      firstError?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 100);
                  }
                }}
                data-testid="template-submit-button"
              >
                {tNs("submit")}
              </LoadingButton>
            </Box>
          </LabRequistionTemplateForm>
        )}
      </DrawerBody>
    </LeftDrawerContainer>
  );
}
